import { Children, useState } from 'react';
import { get } from 'lodash';
import { TextField } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import MuiPhoneNumber from 'material-ui-phone-number';
import { onWheelPreventChangeNumberField } from 'utils/menu';
import { formatNumber } from 'helpers/formPrice';
import { t } from 'i18next';
import {
  MenuSearchField,
  filterMenuChildren,
  countMenuItems,
} from './searchable/searchableMenu';

const SEARCH_THRESHOLD = 1;

const FormTextField = ({
  field,
  parsedError,
  errorProp,
  label,
  variant,
  hidden,
  helperText,
  helperTextParam,
  disabled,
  InputProps,
  inputProps,
  multiline,
  rows,
  select,
  onChange,
  onBlur,
  onKeyPress,
  sx,
  children,
}) => {
  const [search, setSearch] = useState('');

  const formattedValue = field.name.includes('price')
    ? formatNumber(field.value)
    : field.value;

  const showSearch = !!select && countMenuItems(children) > SEARCH_THRESHOLD;
  const menuChildren = showSearch
    ? [
        <MenuSearchField
          key="__menu_search__"
          searchValue={search}
          onSearchChange={setSearch}
        />,
        ...Children.toArray(
          filterMenuChildren(children, search, field.value)
        ),
      ]
    : children;

  return (
    <TextField
      {...field}
      size="small"
      variant={variant}
      hidden={hidden}
      error={!!parsedError || errorProp || false}
      helperText={
        !disabled && typeof helperText !== 'object'
          ? t(parsedError ? parsedError.message : helperTextParam || helperText)
          : helperText
      }
      label={label}
      inputRef={field.ref}
      disabled={disabled}
      InputProps={InputProps}
      onWheel={onWheelPreventChangeNumberField}
      sx={{
        ...sx,
        '& .MuiInputLabel-asterisk': {
          color: 'red',
        },
      }}
      multiline={multiline}
      rows={rows}
      select={select}
      SelectProps={
        showSearch
          ? {
              MenuProps: { autoFocus: false },
              onClose: () => setSearch(''),
            }
          : undefined
      }
      inputProps={inputProps}
      InputLabelProps={{ shrink: true }}
      onChange={onChange || field.onChange}
      onBlur={onBlur || field.onBlur}
      onKeyPress={onKeyPress}
      value={formattedValue ?? ''}
    >
      {menuChildren}
    </TextField>
  );
};

const CustomTextInput = ({
  name,
  id,
  sx,
  children,
  label = '',
  validate,
  inputProps,
  hidden,
  onChange,
  disabled,
  multiline,
  rows,
  select,
  helperText,
  InputProps,

  onKeyPress,
  onBlur,
  variant = 'outlined',
  ...params
}) => {
  const { control, formState } = useFormContext();
  const { errors } = formState;
  let parsedError = get(errors, name);
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        if (
          field?.name?.includes('phone') ||
          field?.name?.includes('legalPhone') ||
          field?.name?.includes('brandPhone') ||
          field?.name?.includes('supportPhone')
        ) {
          return (
            <MuiPhoneNumber
              defaultCountry={'us'}
              variant="outlined"
              {...field}
              size={'small'}
              hidden={hidden}
              error={!!parsedError || params.error || false}
              helperText={
                typeof helperText !== 'object'
                  ? t(
                      parsedError
                        ? parsedError.message
                        : params.helperText || ''
                    ) || ' '
                  : helperText || ' '
              }
              label={label}
              onWheel={onWheelPreventChangeNumberField}
              disabled={disabled}
              InputProps={InputProps}
              sx={{ ...sx, '& svg': { height: '20px' } }}
              InputLabelProps={{ shrink: true }}
            />
          );
        }

        return (
          <FormTextField
            field={field}
            parsedError={parsedError}
            errorProp={params.error}
            label={label}
            variant={variant}
            hidden={hidden}
            helperText={helperText}
            helperTextParam={params.helperText}
            disabled={disabled}
            InputProps={InputProps}
            inputProps={inputProps}
            multiline={multiline}
            rows={rows}
            select={select}
            onChange={onChange}
            onBlur={onBlur}
            onKeyPress={onKeyPress}
            sx={sx}
          >
            {children}
          </FormTextField>
        );
      }}
      {...params}
    />
  );
};

export default CustomTextInput;
